import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  getPackageTrackingListExpandedSelector,
  getPackageTrackingListSelector,
} from '@returnmates/client-core/src/selectors/packages'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import SwaySpinner from '@returnmates/ui-core/src/components/SwaySpinner'
import { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import useStyles from '../styles'
import SelectorButton from './SelectorButton'
import TrackingRow from './TrackingRow'

interface Props {
  packageId?: string | null
}

const StatusHistory = ({ packageId }: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const packageTrackingList = useSelector(getPackageTrackingListSelector)
  const packageTrackingListExpanded = useSelector(getPackageTrackingListExpandedSelector)
  const [isTrackingListExpanded, setIsTrackingListExpanded] = useState(false)
  const [isTrackingListLoading, setIsTrackingListLoading] = useState(false)

  const getTrackingHistory = useCallback(async () => {
    try {
      if (!packageTrackingList?.length) {
        setIsTrackingListLoading(true)

        await createAsyncAction(dispatch, actions.packageTrackingList.request({ packageId }))
      }

      setIsTrackingListExpanded(false)
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsTrackingListLoading(false)
    }
  }, [dispatch, packageId, packageTrackingList?.length])

  const getExpandedTrackingHistory = useCallback(async () => {
    try {
      if (!packageTrackingListExpanded?.length) {
        setIsTrackingListLoading(true)

        await createAsyncAction(
          dispatch,
          actions.packageTrackingListExpanded.request({ packageId, isExpanded: true }),
        )
      }

      setIsTrackingListExpanded(true)
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsTrackingListLoading(false)
    }
  }, [dispatch, packageId, packageTrackingListExpanded?.length])

  useEffect(() => {
    getTrackingHistory()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isTrackingListLoading) {
    return <SwaySpinner classes={{ spinnerWrapper: classes.spinner }} />
  }

  return (
    <>
      <SelectorButton
        getTrackingHistory={getTrackingHistory}
        getExpandedTrackingHistory={getExpandedTrackingHistory}
        isTrackingListExpanded={isTrackingListExpanded}
      />
      <Table className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow className={classes.headerRow}>
            <TableCell className={classes.headerCell}>Date</TableCell>
            <TableCell className={classes.headerCell}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isTrackingListExpanded
            ? packageTrackingListExpanded?.map(tracking => (
                <TrackingRow key={tracking.id} tracking={tracking} />
              ))
            : packageTrackingList?.map(tracking => (
                <TrackingRow key={tracking.id} tracking={tracking} />
              ))}
        </TableBody>
      </Table>
    </>
  )
}

export default memo(StatusHistory)
